/**
 * Precision Hero Component
 * Handles thumbnail navigation with carousel-swiper
 */

import CarouselSwiper from '../components/carousel-swiper.js';
import initVariantPicker from '../components/precision-variant-picker.js';

// Register the custom element if not already registered
if (!customElements.get('carousel-swiper')) {
  customElements.define('carousel-swiper', CarouselSwiper);
}

function initPrecisionHero() {
  // Find all precision hero sections
  const sections = document.querySelectorAll('[data-section-id]');
  
  sections.forEach((sectionEl) => {
    // Check if this is a precision hero section
    if (!sectionEl.classList.toString().includes('precision-hero-')) return;
    
    const sectionId = sectionEl.dataset.sectionId;
    if (!sectionId) return;
    
    // Wait for carousel-swiper to initialize
    const carouselSwiper = sectionEl.querySelector('carousel-swiper');
    if (!carouselSwiper) return;
    
    // Wait for Swiper to be initialized by carousel-swiper
    const checkSwiper = setInterval(() => {
      if (carouselSwiper.swiper) {
        clearInterval(checkSwiper);
        setupThumbnailNavigation(sectionEl, sectionId, carouselSwiper.swiper);
        setupImageZoom(sectionEl);
      }
    }, 100);
    
    // Timeout after 2 seconds
    setTimeout(() => clearInterval(checkSwiper), 2000);
  });
}

// Image zoom functionality
function setupImageZoom(sectionEl) {
  const images = sectionEl.querySelectorAll('.image-magnify-hover');
  const zoomRatio = 2;

  images.forEach(image => {
    // Skip if already initialized
    if (image.dataset._zoomAttached) return;
    image.dataset._zoomAttached = true;

    image.addEventListener('click', (event) => {
      magnifyImage(image, zoomRatio, event);
    });
  });
}

function magnifyImage(image, zoomRatio, clickEvent) {
  // Get the full-size image URL from srcset or currentSrc
  let fullSizeUrl = image.currentSrc || image.src;
  
  // Try to get largest image from srcset
  const srcset = image.getAttribute('srcset');
  if (srcset) {
    const sources = srcset.split(',').map(s => s.trim());
    // Get the last (largest) source
    const lastSource = sources[sources.length - 1];
    if (lastSource) {
      fullSizeUrl = lastSource.split(' ')[0];
    }
  }

  // Create overlay
  const overlay = document.createElement('div');
  overlay.setAttribute('class', 'image-magnify-full-size');
  overlay.setAttribute('aria-hidden', 'true');
  overlay.style.backgroundImage = `url('${fullSizeUrl}')`;
  overlay.style.backgroundColor = 'var(--gradient-background, #fff)';

  // Show loading state
  image.style.opacity = '50%';
  const spinner = image.closest('.product__media')?.querySelector('.loading-overlay__spinner');
  if (spinner) spinner.classList.remove('hidden');

  // Create temp image to check load
  const tempImg = new Image();
  tempImg.src = fullSizeUrl;
  
  tempImg.onload = () => {
    if (spinner) spinner.classList.add('hidden');
    image.style.opacity = '100%';
    image.parentElement.insertBefore(overlay, image);
    
    // Initial position based on click
    moveWithHover(image, clickEvent, zoomRatio, overlay);
  };

  tempImg.onerror = () => {
    if (spinner) spinner.classList.add('hidden');
    image.style.opacity = '100%';
  };

  // Overlay event handlers
  overlay.onclick = () => overlay.remove();
  overlay.onmousemove = (event) => moveWithHover(image, event, zoomRatio, overlay);
  overlay.onmouseleave = () => overlay.remove();
}

function moveWithHover(image, event, zoomRatio, overlay) {
  const ratio = image.height / image.width;
  const container = event.target.getBoundingClientRect();
  const xPosition = event.clientX - container.left;
  const yPosition = event.clientY - container.top;
  const xPercent = `${xPosition / (image.clientWidth / 100)}%`;
  const yPercent = `${yPosition / ((image.clientWidth * ratio) / 100)}%`;

  overlay.style.backgroundPosition = `${xPercent} ${yPercent}`;
  overlay.style.backgroundSize = `${image.width * zoomRatio}px`;
}

function setupThumbnailNavigation(sectionEl, sectionId, swiper) {
  // Get thumbnails
  const thumbnails = sectionEl.querySelectorAll('.precision-hero-thumbnail-' + sectionId);
  
  if (thumbnails.length === 0) return;
  
  // Update active thumbnail on slide change
  function updateActiveThumbnail(activeIndex) {
    thumbnails.forEach((thumb) => {
      const mediaIndex = parseInt(thumb.dataset.mediaIndex);
      if (mediaIndex === activeIndex) {
        thumb.style.border = '1.14px solid #bfbfbf';
        thumb.classList.add('tw-shadow-[0px_4.417px_7.572px_0px_rgba(0,0,0,0.1)]');
        thumb.classList.remove('tw-border-[#bfbfbf]');
      } else {
        thumb.style.border = 'none';
        thumb.classList.remove('tw-shadow-[0px_4.417px_7.572px_0px_rgba(0,0,0,0.1)]');
        thumb.classList.add('tw-border-[#bfbfbf]');
      }
    });
  }
  
  // Listen to Swiper slide change events
  swiper.on('slideChange', function() {
    updateActiveThumbnail(this.activeIndex);
  });
  
  // Thumbnail click handlers - navigate to corresponding slide
  thumbnails.forEach((thumb) => {
    thumb.addEventListener('click', () => {
      const mediaIndex = parseInt(thumb.dataset.mediaIndex);
      swiper.slideTo(mediaIndex);
    });
  });
  
  // Initialize first thumbnail as active
  updateActiveThumbnail(0);
}

function initProductDescriptionToggle() {
  // Each collapsible description wrapper rendered when "Read More" is enabled
  // in the section settings. No-ops when the feature is off (no markup present).
  const wrappers = document.querySelectorAll('[data-product-description]');

  wrappers.forEach((wrapper) => {
    const content = wrapper.querySelector('[data-description-content]');
    const toggleButton = wrapper.querySelector('[data-description-toggle]');
    if (!content || !toggleButton) return;

    // Only reveal the toggle when the content actually overflows the clamp.
    const isOverflowing = content.scrollHeight > content.clientHeight + 1;
    if (!isOverflowing) {
      toggleButton.hidden = true;
      return;
    }
    toggleButton.hidden = false;

    toggleButton.addEventListener('click', () => {
      const isExpanded = wrapper.classList.toggle('is-expanded');
      toggleButton.textContent = isExpanded ? 'Read less' : 'Read more';
      toggleButton.setAttribute('aria-expanded', String(isExpanded));
    });
  });
}

// Initialize both functions
function initPrecisionHeroComplete() {
  initPrecisionHero();
  initProductDescriptionToggle();
  initVariantPicker();
}

export default initPrecisionHeroComplete;

